import React, { lazy, Suspense, useMemo, useState } from 'react'
import { Alert, Button, Modal, Select, Space, Spin, Tooltip } from 'antd'
import { EyeOutlined, MinusOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { normalizePreviewFiles, PreviewFile, previewKind, PreviewDocumentLoader } from './files'
import DownloadButton from './download-button'
import { PreviewBody, PreviewButton } from './styles'

const DocumentPreview = lazy(() => import('./document-preview'))

export function FileContent({ file, page, isolatePdfPage = true, pdfView, canvasPdf = false, documentLoader }: { file: PreviewFile; page?: number; isolatePdfPage?: boolean; pdfView?: 'Fit' | 'FitH'; canvasPdf?: boolean; documentLoader?: PreviewDocumentLoader }) {
  const [failed, setFailed] = useState(false)
  const [scale, setScale] = useState(1)
  const kind = previewKind(file)
  if (failed)
    return (
      <Alert
        type="warning"
        showIcon
        message="Не удалось показать файл"
        description="Формат может не поддерживаться браузером или файл недоступен. Попробуйте открыть его в новой вкладке или скачать."
      />
    )
  if (kind === 'image')
    return (
      <>
        <Space>
          <Button
            icon={<MinusOutlined />}
            aria-label="Уменьшить"
            disabled={scale <= 0.5}
            onClick={() => setScale(value => Math.max(0.5, value - 0.25))}
          />
          <span>{Math.round(scale * 100)}%</span>
          <Button
            icon={<PlusOutlined />}
            aria-label="Увеличить"
            disabled={scale >= 3}
            onClick={() => setScale(value => Math.min(3, value + 0.25))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => setScale(1)}>
            По размеру окна
          </Button>
        </Space>
        <div className="file-preview-stage">
          <img
            className="file-preview-image"
            alt={file.name}
            src={file.url}
            onError={() => setFailed(true)}
            style={{ width: `${scale * 100}%`, height: `${scale * 100}%`, maxWidth: 'none' }}
          />
        </div>
      </>
    )
  if (kind === 'video')
    return (
      <div className="file-preview-stage">
        <video
          controls
          playsInline
          preload="metadata"
          src={file.url}
          onError={() => setFailed(true)}
        />
      </div>
    )
  if (kind === 'audio')
    return (
      <div className="file-preview-stage file-preview-center">
        <audio controls preload="metadata" src={file.url} onError={() => setFailed(true)} />
      </div>
    )
  if (kind === 'unsupported')
    return (
      <Alert
        type="info"
        showIcon
        message="Для этого формата встроенный просмотр недоступен"
        description="Скачайте файл и откройте его в подходящей программе. В окне можно просматривать изображения, видео, аудио, PDF, DOCX, Excel и текстовые файлы."
      />
    )
  return (
    <Suspense
      fallback={
        <div className="file-preview-center">
          <Spin />
        </div>
      }
    >
      <DocumentPreview file={file} kind={kind} page={page} isolatePdfPage={isolatePdfPage} pdfView={pdfView} canvasPdf={canvasPdf} documentLoader={documentLoader} />
    </Suspense>
  )
}

export default function FilePreview({
  files,
  buttonClassName,
}: {
  files: unknown
  buttonClassName?: string
}) {
  const normalized = useMemo(() => normalizePreviewFiles(files), [files])
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const file = normalized[index] || normalized[0]
  if (!file) return null

  return (
    <>
      <Tooltip title="Просмотреть файл">
        <PreviewButton
          className={buttonClassName}
          type="text"
          icon={<EyeOutlined />}
          aria-label={`Просмотреть файл: ${file.name}`}
          onClick={event => {
            event.stopPropagation()
            setIndex(0)
            setOpen(true)
          }}
        />
      </Tooltip>
      {open && (
        <Modal
          open
          onCancel={() => setOpen(false)}
          title={<div style={{ paddingRight: 28, overflowWrap: 'anywhere' }}>{file.name}</div>}
          width={1120}
          style={{ top: 24, maxWidth: 'calc(100vw - 24px)' }}
          footer={
            <Space wrap>
              <Button href={file.url} target="_blank" rel="noopener noreferrer">
                Открыть в новой вкладке
              </Button>
              <DownloadButton key={file.url} file={file} />
              <Button onClick={() => setOpen(false)}>Закрыть</Button>
            </Space>
          }
        >
          <PreviewBody>
            {normalized.length > 1 && (
              <Select
                aria-label="Выбрать файл"
                value={Math.min(index, normalized.length - 1)}
                onChange={setIndex}
                options={normalized.map((item, value) => ({
                  value,
                  label: `${value + 1}. ${item.name}`,
                }))}
              />
            )}
            <FileContent key={`${file.url}:${file.name}`} file={file} />
          </PreviewBody>
        </Modal>
      )}
    </>
  )
}
